import classNames from "classnames";
import parse from "html-react-parser";
import React, { useMemo } from "react";

import type { IBlogAllQueryNode } from "@repo/shopify/queries/blog-gql";
import type { Video as VideoType } from "@repo/shopifyV2/types/storefront.types";

import Image from "@atoms/Image";
import Link from "@atoms/Link";
import Video from "@atoms/Video";

interface IProps {
  node: IBlogAllQueryNode;
  isLarge?: boolean;
  category: "all" | "videos" | "lookbook" | "news";
}

export default function FeaturesCard({ node, category, isLarge = true }: IProps) {
  const videoSource = useMemo(() => {
    if (node.blog.handle !== "videos") return;

    return (node.video?.reference as VideoType)?.sources?.find((item) => item.format === "mp4");
  }, [node]);

  return (
    <Link
      className={classNames(isLarge ? "flex! w-full flex-col" : "pc:gap-x-5 gap-x-2.5")}
      href={
        category === "all" && node.blog.handle === "videos"
          ? `/features?handle=${node.handle}`
          : `/features/${node.blog.handle}/${node.handle}`
      }
      key={node.id}
    >
      <div className={classNames(isLarge ? "flex! w-full flex-col gap-2" : "pc:gap-x-5 grid! grid-cols-3 gap-x-2.5")}>
        {node.blog.handle === "videos" ? (
          <>
            {category === "all" ? (
              <>
                <div className="flex aspect-4/5 items-center justify-center overflow-hidden bg-black p-2">
                  {videoSource ? (
                    <Video height={videoSource?.height || 1800} src={videoSource?.url || ""} width={videoSource?.width || 1440} />
                  ) : (
                    <Image
                      alt={node.image?.altText || ""}
                      className="object-contain"
                      height={node.image?.height || 500}
                      src={node.image?.url || ""}
                      width={node.image?.width || 400}
                    />
                  )}

                  {node.excerptHtml ? (
                    <div className="absolute bottom-2 left-3 font-normal text-white">{parse(node.excerptHtml)}</div>
                  ) : null}
                </div>

                <div className="col-span-2 flex flex-col gap-0.5">
                  <span className="capitalize">{node.blog.handle}</span>
                  <span>{node.title}</span>
                </div>
              </>
            ) : (
              <>
                <Image
                  alt={node.image?.altText || ""}
                  className="object-contain"
                  height={node.image?.height || 500}
                  src={node.image?.url || ""}
                  width={node.image?.width || 400}
                />

                <div className="flex flex-row justify-start text-start">
                  <span className="pc:min-w-15 min-w-12">{parse(node.excerptHtml || "")}</span>
                  <span style={{ overflowWrap: "anywhere" }}>{node.title}</span>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <div className="relative aspect-4/5 w-full overflow-hidden bg-gray-100">
              <Image
                alt={node.image?.altText || ""}
                className="h-full w-full object-cover"
                height={node.image?.height || 500}
                src={node.image?.url || ""}
                width={node.image?.width || 400}
              />
            </div>

            <div className="col-span-2 flex flex-col gap-0.5 font-normal">
              {category === "all" && <span className="font-bold capitalize">{node.blog.handle}</span>}
              <span className="font-bold">{parse(node.title || "")}</span>
              {node.excerptHtml ? <>{parse(node.excerptHtml || "")}</> : null}
            </div>
          </>
        )}
      </div>
    </Link>
  );
}
